// Destructive-delete confirmation for a screen. Soft-delete (trash/restore)
// is an internal-admin-only action, not exposed to operators here — see
// backend/app/admin/routes/screens.py.
//
// This used to be a plain "are you sure" with one sentence and a red button.
// Two problems with that on the most destructive action an operator has:
//
//   1. It never said what is actually lost. Deleting takes the content
//      assignments and the whole activity history with the row, and operators
//      reached for it when they only wanted to swap a broken panel — the
//      lighter Detach and Relink now sit directly above it, so the modal
//      points at them by name.
//   2. A single click cannot be undone. Typing the screen's name is the
//      cheapest friction that forces the operator to read WHICH screen they
//      are about to remove, which is the mistake that actually happens on a
//      page you reach from a list of near-identical names.
//
// It also states the one reassuring fact nobody knew: the licence seat is
// freed back to the pool with its term intact, not burned.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Trash2, AlertTriangle } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'

interface Props {
  open:               boolean
  screenName:         string
  onClose:            () => void
  /** Wired to the existing DELETE /screens/:id endpoint. */
  onDeletePermanent:  () => Promise<void> | void
  /** Offered as the non-destructive alternative when hardware is attached. */
  onDetachInstead?:   () => void
  hasDevice?:         boolean
}

export default function DeleteScreenDialog({
  open, screenName, onClose, onDeletePermanent, onDetachInstead, hasDevice,
}: Props) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState('')

  // Clear between openings — a name left in the box from a previous screen
  // would let the next delete through with no friction at all.
  useEffect(() => { if (open) setTyped('') }, [open])

  const nameMatches = typed.trim() === screenName.trim() && screenName.trim() !== ''

  const doPermanent = async () => {
    if (!nameMatches) return
    setBusy(true)
    try { await onDeletePermanent(); onClose() }
    catch (err: any) { toast.error(err?.response?.data?.detail || t('delete.failed', 'Delete failed.')) }
    finally { setBusy(false) }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o && !busy) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-4 h-4" />
            {t('delete.title', 'Delete Screen')}
          </DialogTitle>
          <DialogDescription>
            {t('delete.confirmDescription2',
              'Permanently remove "{{name}}". This cannot be undone.',
              { name: screenName })}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-destructive mb-2">
            {t('delete.whatHappens', 'What this removes')}
          </p>
          <ul className="text-sm space-y-1.5 list-disc ps-5">
            <li>{t('delete.loss1', 'The screen and everything published to it.')}</li>
            <li>{t('delete.loss2', 'Its activity history and screenshots.')}</li>
            <li>{t('delete.loss3', 'The attached device unlinks and drops to a pairing code.')}</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2.5">
            {t('delete.licenceKept', 'Your licence seat is not lost — it returns to the pool with its remaining term and can be used on another screen.')}
          </p>
        </div>

        {/* The most common reason to be in this modal by mistake. */}
        {hasDevice && onDetachInstead && (
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-sm">
              {t('delete.detachInsteadQ', 'Only replacing the hardware?')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('delete.detachInsteadDesc', 'Detach or Relink keeps this screen, its content and its licence.')}
            </p>
            <Button
              variant="outline" size="sm" className="mt-2"
              onClick={() => { onClose(); onDetachInstead() }}
              disabled={busy}
            >
              {t('screens.detach', 'Detach')}
            </Button>
          </div>
        )}

        <div className="grid gap-1.5">
          <Label htmlFor="delete-confirm-name">
            {t('delete.typeToConfirm', 'Type {{name}} to confirm', { name: screenName })}
          </Label>
          <Input
            id="delete-confirm-name"
            value={typed}
            onChange={e => setTyped(e.target.value)}
            autoComplete="off"
            placeholder={screenName}
            disabled={busy}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="destructive" onClick={doPermanent}
            disabled={busy || !nameMatches} className="gap-1.5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {t('delete.permanently', 'Delete Permanently')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
